import React, { useState, useEffect } from 'react';

const PodPanel = ({ isOpen, onClose }) => {
  const [podLoaded, setPodLoaded] = useState(false);
  const POD_URL = 'https://fundingpath.emergent.host';

  useEffect(() => {
    if (isOpen && !podLoaded) {
      setPodLoaded(true);
    }
  }, [isOpen, podLoaded]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Preload iframe after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!podLoaded) {
        setPodLoaded(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [podLoaded]);

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] transition-all duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={onClose}
      />

      {/* Full Page Panel */}
      <div 
        className={`fixed top-0 h-full w-full bg-[#0a0f1a] z-[1000000] flex flex-col transition-all duration-[400ms] ${
          isOpen ? 'right-0' : '-right-full'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 md:px-5 border-b border-white/10 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(59, 130, 246, 0.15))' }}
        >
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_oneforce-wizard/artifacts/eep2boyt_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png"
              alt="AI Horse-Powered"
              className="w-10 h-10 rounded-full object-contain"
            />
            <div>
              <span className="text-white text-lg font-bold block" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
                AI Horse-Powered POD
              </span>
              <small className="text-cyan-400 text-xs font-medium">
                Your Business Service Center
              </small>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/10 text-slate-400 flex items-center justify-center hover:bg-red-500/20 hover:text-red-400 transition-all"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Iframe */}
        <iframe 
          src={podLoaded ? POD_URL : 'about:blank'}
          className="w-full flex-1 border-none bg-[#0a0f1a]"
          allow="clipboard-write; payment; camera; microphone; fullscreen"
          title="AI Horse-Powered POD"
        />
      </div>
    </>
  );
};

// Trigger Button Component - Keep the same exact look with speech bubble
export const PodTriggerButton = ({ onClick }) => {
  const [showBubble, setShowBubble] = React.useState(true);

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 z-[999998] flex flex-col items-end">
      {/* Pre-Approved Speech Bubble - Comic Style matching reference */}
      {showBubble && (
      <div className="mb-1 relative">
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowBubble(false);
          }}
          className="absolute -top-1 -right-1 w-5 h-5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-full flex items-center justify-center z-10 transition-colors shadow-md"
          aria-label="Close bubble"
        >
          ×
        </button>
        <svg 
          width="190" 
          height="90" 
          viewBox="0 0 190 90" 
          className="drop-shadow-lg"
        >
          {/* Black shadow offset for 3D comic effect */}
          <path 
            d="M18 6 
               C10 6 6 10 6 18 
               L6 46 
               C6 54 10 58 18 58 
               L130 58
               C133 58 135 59 136 62
               C137 65 136 68 134 72
               C132 76 134 80 138 84
               C142 88 148 88 148 88
               C144 84 142 80 144 76
               C146 72 150 68 152 64
               C154 60 152 58 156 58
               L172 58 
               C180 58 184 54 184 46 
               L184 18 
               C184 10 180 6 172 6 
               Z"
            fill="#2a2a2a"
            transform="translate(3, 3)"
          />
          {/* White bubble main body with curvy tail */}
          <path 
            d="M18 6 
               C10 6 6 10 6 18 
               L6 46 
               C6 54 10 58 18 58 
               L130 58
               C133 58 135 59 136 62
               C137 65 136 68 134 72
               C132 76 134 80 138 84
               C142 88 148 88 148 88
               C144 84 142 80 144 76
               C146 72 150 68 152 64
               C154 60 152 58 156 58
               L172 58 
               C180 58 184 54 184 46 
               L184 18 
               C184 10 180 6 172 6 
               Z"
            fill="white"
            stroke="#1a1a1a"
            strokeWidth="2.5"
          />
          {/* Text */}
          <text x="95" y="28" textAnchor="middle" fill="#1a1a1a" fontSize="15" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif">
            Pre-Approved?
          </text>
          <text x="95" y="48" textAnchor="middle" fill="#1a1a1a" fontSize="15" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif">
            Start Free Trial
          </text>
        </svg>
      </div>
      )}
      
      {/* POD Button */}
      <button 
        onClick={onClick}
        className="w-14 h-14 sm:w-16 sm:h-16 md:w-[70px] md:h-[70px] lg:w-20 lg:h-20 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center cursor-pointer shadow-[0_8px_30px_rgba(34,211,238,0.5)] hover:scale-110 hover:shadow-[0_12px_40px_rgba(34,211,238,0.6)] transition-all animate-pulse-cyan"
        aria-label="Open AI Horse-Powered POD"
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_marketai-hub-17/artifacts/zw4k9iu6_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png"
          alt="Open POD"
          className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full object-contain"
        />
      </button>
    </div>
  );
};

export default PodPanel;
